import Feature from 'ol/Feature';
import type Geometry from 'ol/geom/Geometry';
import MultiPolygon from 'ol/geom/MultiPolygon';
import Polygon from 'ol/geom/Polygon';
import polygonClipping, { type MultiPolygon as ClippingMultiPolygon, type Polygon as ClippingPolygon } from 'polygon-clipping';
import type {
  AttributeValue,
  FieldDescriptor,
  LayerModel,
  OperationalAttributeField,
  OperationalSubarea,
  OriginalAttributes,
  VectorFeatureRecord,
  VectorLayerModel,
} from '../core/types';
import { measureArea } from './measurement';
import { normalizeLayerOrder } from './layers';

export const SUBAREA_LAYER_ID = 'operational-subareas';
export const SUBAREA_OVERLAP_TOLERANCE_M2 = 0.01;
/** Below this area a remaining part is numerical residue, the same threshold used for overlaps between subareas. */
export const SUBAREA_REMAINDER_TOLERANCE_M2 = SUBAREA_OVERLAP_TOLERANCE_M2;
const { difference, intersection, union } = polygonClipping;

export interface SubareaParent {
  readonly level: 'suerte' | 'tablon';
  readonly id: string;
}

export interface SubareaPreview {
  readonly parent: SubareaParent;
  readonly parentGeometry: Polygon | MultiPolygon;
  readonly geometry: Polygon | MultiPolygon;
  readonly fincaId: string;
  readonly fincaName: string;
  readonly codfinca: string;
  readonly suerteId: string;
  readonly codSuerte: string;
  readonly tablonId?: string;
  readonly tablon?: string;
  readonly parentGeometryAreaM2: number;
  readonly parentDeclaredAreaHa?: number;
  readonly drawnAreaM2: number;
  readonly effectiveAreaM2: number;
  readonly discardedAreaM2: number;
  readonly coveragePercent: number;
  readonly resultPartCount: number;
  /** Present when the geometry was derived by "Crear área restante" instead of being drawn. */
  readonly remaining?: {
    readonly coveredBeforeAreaM2: number;
    readonly coverageBeforePercent: number;
    readonly existingCount: number;
  };
}

export interface ParentCoverage {
  readonly parent: SubareaParent;
  readonly parentGeometryAreaM2: number;
  readonly parentDeclaredAreaHa?: number;
  readonly coveredAreaM2: number;
  readonly remainingAreaM2: number;
  readonly coveredPercent: number;
  readonly subareaCount: number;
}

const asClipping = (geometry: Geometry): ClippingPolygon | ClippingMultiPolygon => {
  if (geometry instanceof Polygon) return geometry.getCoordinates() as ClippingPolygon;
  if (geometry instanceof MultiPolygon) return geometry.getCoordinates() as ClippingMultiPolygon;
  throw new Error('La operación parcial requiere geometrías Polygon o MultiPolygon.');
};

const fromClipping = (coordinates: ClippingMultiPolygon): Polygon | MultiPolygon => {
  if (!coordinates.length) throw new Error('El dibujo no intersecta el límite lógico seleccionado.');
  return coordinates.length === 1 ? new Polygon(coordinates[0]!) : new MultiPolygon(coordinates);
};

export function logicalParentGeometry(model: LayerModel, parent: SubareaParent): Polygon | MultiPolygon {
  const entity = parent.level === 'suerte' ? model.suertes.get(parent.id) : model.tablones.get(parent.id);
  if (!entity) throw new Error('No se encontró la Suerte/Tablón seleccionado.');
  const polygons = entity.fragmentIds.flatMap((id) => {
    const geometry = model.fragmentsById.get(id)?.feature.getGeometry();
    return geometry instanceof Polygon || geometry instanceof MultiPolygon ? [asClipping(geometry)] : [];
  });
  if (!polygons.length) throw new Error('El padre lógico no contiene geometría poligonal válida.');
  return fromClipping(union(polygons[0]!, ...polygons.slice(1)));
}

interface ParentContext {
  readonly parentGeometry: Polygon | MultiPolygon;
  readonly parentGeometryAreaM2: number;
  readonly parentDeclaredAreaHa?: number;
  readonly identity: Pick<SubareaPreview, 'fincaId' | 'fincaName' | 'codfinca' | 'suerteId' | 'codSuerte' | 'tablonId' | 'tablon'>;
}

function parentContext(model: LayerModel, parent: SubareaParent): ParentContext {
  const parentGeometry = logicalParentGeometry(model, parent);
  const entity = parent.level === 'suerte' ? model.suertes.get(parent.id)! : model.tablones.get(parent.id)!;
  const firstId = entity.fragmentIds[0];
  const fragment = firstId ? model.fragmentsById.get(firstId) : undefined;
  if (!fragment) throw new Error('El padre lógico no contiene fragmentos verificables.');
  const suerte = model.suertes.get(fragment.suerteId)!;
  const finca = model.fincas.get(fragment.fincaId)!;
  const tablon = parent.level === 'tablon' ? model.tablones.get(parent.id)! : undefined;
  const declared = Number(entity.declaredAreaHa);
  return {
    parentGeometry,
    parentGeometryAreaM2: measureArea(parentGeometry).areaM2,
    ...(Number.isFinite(declared) && declared > 0 ? { parentDeclaredAreaHa: declared } : {}),
    identity: { fincaId: finca.id, fincaName: finca.name, codfinca: finca.code, suerteId: suerte.id, codSuerte: suerte.code, ...(tablon ? { tablonId: tablon.id, tablon: tablon.code } : {}) },
  };
}

const partCount = (geometry: Polygon | MultiPolygon): number => geometry instanceof MultiPolygon ? geometry.getPolygons().length : 1;

export function createSubareaPreview(model: LayerModel, parent: SubareaParent, drawn: Geometry): SubareaPreview {
  if (!(drawn instanceof Polygon || drawn instanceof MultiPolygon)) throw new Error('Dibuja un polígono o rectángulo para crear el área parcial.');
  const context = parentContext(model, parent);
  const clipped = fromClipping(intersection(asClipping(drawn), asClipping(context.parentGeometry)));
  const drawnMeasurement = measureArea(drawn);
  const effectiveMeasurement = measureArea(clipped);
  if (effectiveMeasurement.areaM2 <= 0) throw new Error('El dibujo no intersecta el límite lógico seleccionado.');
  return {
    parent,
    parentGeometry: context.parentGeometry,
    geometry: clipped,
    ...context.identity,
    parentGeometryAreaM2: context.parentGeometryAreaM2,
    ...(context.parentDeclaredAreaHa !== undefined ? { parentDeclaredAreaHa: context.parentDeclaredAreaHa } : {}),
    drawnAreaM2: drawnMeasurement.areaM2,
    effectiveAreaM2: effectiveMeasurement.areaM2,
    discardedAreaM2: Math.max(0, drawnMeasurement.areaM2 - effectiveMeasurement.areaM2),
    coveragePercent: context.parentGeometryAreaM2 > 0 ? effectiveMeasurement.areaM2 / context.parentGeometryAreaM2 * 100 : 0,
    resultPartCount: partCount(clipped),
  };
}

/**
 * Partition of one parent's ACTIVE geometry by its own subareas (same parentLevel + parentId only):
 * - covered = area(parent ∩ union(subareas)): a stored subarea may extend beyond a parent edited later, that
 *   outside part is ignored here and never cut from the stored subarea;
 * - remaining = parent − union(subareas), keeping only parts larger than SUBAREA_REMAINDER_TOLERANCE_M2.
 * Memoized per (active model, subareas list, parent): the panel and the inspector reuse it on every render.
 */
interface ParentPartition {
  readonly context: ParentContext;
  readonly subareaCount: number;
  readonly coveredAreaM2: number;
  readonly remaining: Polygon | MultiPolygon | null;
  readonly remainingAreaM2: number;
}
const partitionCache = new WeakMap<LayerModel, WeakMap<readonly OperationalSubarea[], Map<string, ParentPartition>>>();

function parentPartition(model: LayerModel, parent: SubareaParent, subareas: readonly OperationalSubarea[]): ParentPartition {
  const key = `${parent.level}:${parent.id}`;
  const cached = partitionCache.get(model)?.get(subareas)?.get(key);
  if (cached) return cached;
  const context = parentContext(model, parent);
  const matching = subareas.filter((item) => item.parentLevel === parent.level && item.parentId === parent.id);
  const geometries = matching.flatMap((item) => {
    const geometry = item.feature.getGeometry();
    return geometry instanceof Polygon || geometry instanceof MultiPolygon ? [asClipping(geometry)] : [];
  });
  const parentCoordinates = asClipping(context.parentGeometry);
  const occupied = geometries.length ? intersection(parentCoordinates, union(geometries[0]!, ...geometries.slice(1))) : [];
  const coveredAreaM2 = occupied.length ? measureArea(fromClipping(occupied)).areaM2 : 0;
  const rawRemaining = geometries.length ? difference(parentCoordinates, ...geometries) : (context.parentGeometry instanceof Polygon ? [parentCoordinates as ClippingPolygon] : parentCoordinates as ClippingMultiPolygon);
  const parts = rawRemaining.map((coordinates) => ({ coordinates, areaM2: measureArea(new Polygon(coordinates)).areaM2 })).filter(({ areaM2 }) => areaM2 > SUBAREA_REMAINDER_TOLERANCE_M2);
  const remaining = parts.length ? fromClipping(parts.map(({ coordinates }) => coordinates)) : null;
  const partition = { context, subareaCount: matching.length, coveredAreaM2, remaining, remainingAreaM2: remaining ? measureArea(remaining).areaM2 : 0 };
  const byModel = partitionCache.get(model) ?? new WeakMap<readonly OperationalSubarea[], Map<string, ParentPartition>>();
  const byList = byModel.get(subareas) ?? new Map<string, ParentPartition>();
  byList.set(key, partition); byModel.set(subareas, byList); partitionCache.set(model, byModel);
  return partition;
}

/**
 * "Crear área restante": RESTO = geometría activa del padre − unión de sus áreas parciales (mismo padre).
 * The result is an ordinary preview for createOperationalSubarea: nothing is linked to the existing subareas,
 * and neither the Maestro nor the active geometry is modified.
 */
export function createRemainingSubareaPreview(model: LayerModel, parent: SubareaParent, subareas: readonly OperationalSubarea[]): SubareaPreview {
  const partition = parentPartition(model, parent, subareas);
  if (!partition.subareaCount) throw new Error('Crea primero un área parcial en este padre para calcular el área restante.');
  if (!partition.remaining) throw new Error(`No queda área geométrica disponible en ${parent.level === 'suerte' ? 'esta Suerte' : 'este Tablón'}.`);
  const { context, remaining, remainingAreaM2, coveredAreaM2 } = partition;
  const percent = (area: number) => context.parentGeometryAreaM2 > 0 ? area / context.parentGeometryAreaM2 * 100 : 0;
  return {
    parent,
    parentGeometry: context.parentGeometry,
    geometry: remaining.clone(),
    ...context.identity,
    parentGeometryAreaM2: context.parentGeometryAreaM2,
    ...(context.parentDeclaredAreaHa !== undefined ? { parentDeclaredAreaHa: context.parentDeclaredAreaHa } : {}),
    drawnAreaM2: remainingAreaM2,
    effectiveAreaM2: remainingAreaM2,
    discardedAreaM2: 0,
    coveragePercent: percent(remainingAreaM2),
    resultPartCount: partCount(remaining),
    remaining: { coveredBeforeAreaM2: coveredAreaM2, coverageBeforePercent: percent(coveredAreaM2), existingCount: partition.subareaCount },
  };
}

export function overlapAreaM2(preview: SubareaPreview, existing: readonly OperationalSubarea[]): number {
  return existing.filter((item) => item.parentLevel === preview.parent.level && item.parentId === preview.parent.id).reduce((sum, item) => {
    const geometry = item.feature.getGeometry();
    if (!(geometry instanceof Polygon || geometry instanceof MultiPolygon)) return sum;
    const overlap = intersection(asClipping(preview.geometry), asClipping(geometry));
    return overlap.length ? sum + measureArea(fromClipping(overlap)).areaM2 : sum;
  }, 0);
}

export function assertNoSubareaOverlap(preview: SubareaPreview, existing: readonly OperationalSubarea[], toleranceM2 = SUBAREA_OVERLAP_TOLERANCE_M2): void {
  const area = overlapAreaM2(preview, existing);
  if (area > toleranceM2) throw new Error(`La subárea se superpone ${area.toLocaleString('es-NI', { maximumFractionDigits: 2 })} m² con otra subárea del mismo padre. El guardado fue bloqueado.`);
}

export function createOperationalSubarea(preview: SubareaPreview, name: string, existing: readonly OperationalSubarea[], id: string = globalThis.crypto?.randomUUID?.() ?? `subarea-${Date.now().toString(36)}`): OperationalSubarea {
  assertNoSubareaOverlap(preview, existing);
  const feature = new Feature({ geometry: preview.geometry.clone() });
  feature.setId(id);
  feature.set('genericFeatureId', id, true);
  return {
    id,
    name: name.trim() || `Área parcial ${existing.length + 1}`,
    parentLevel: preview.parent.level,
    parentId: preview.parent.id,
    fincaId: preview.fincaId,
    fincaName: preview.fincaName,
    codfinca: preview.codfinca,
    suerteId: preview.suerteId,
    codSuerte: preview.codSuerte,
    ...(preview.tablonId ? { tablonId: preview.tablonId, tablon: preview.tablon } : {}),
    feature,
    createdAt: new Date().toISOString(),
    areaM2: preview.effectiveAreaM2,
    areaHa: preview.effectiveAreaM2 / 10_000,
    coveragePercent: preview.coveragePercent,
    parentGeometryAreaM2: preview.parentGeometryAreaM2,
    ...(preview.parentDeclaredAreaHa !== undefined ? { parentDeclaredAreaHa: preview.parentDeclaredAreaHa } : {}),
  };
}

export function coverageForParent(model: LayerModel, parent: SubareaParent, subareas: readonly OperationalSubarea[]): ParentCoverage {
  const { context, coveredAreaM2, remainingAreaM2, subareaCount } = parentPartition(model, parent, subareas);
  return {
    parent,
    parentGeometryAreaM2: context.parentGeometryAreaM2,
    ...(context.parentDeclaredAreaHa !== undefined ? { parentDeclaredAreaHa: context.parentDeclaredAreaHa } : {}),
    coveredAreaM2,
    remainingAreaM2,
    coveredPercent: context.parentGeometryAreaM2 > 0 ? coveredAreaM2 / context.parentGeometryAreaM2 * 100 : 0,
    subareaCount,
  };
}

export const subareaSourceFields = ['Nombre', 'Finca', 'Codfinca', 'CodSuerte', 'Tablon', 'Nivel_padre', 'Area_geometrica_m2', 'Area_geometrica_ha', 'Cobertura_pct', 'Area_padre_geometrica_ha', 'Area_padre_declarada_ha', 'Creado'] as const;

export function subareaFieldDescriptors(fields: readonly OperationalAttributeField[]): readonly FieldDescriptor[] {
  const sourceTypes: Record<(typeof subareaSourceFields)[number], FieldDescriptor['type']> = {
    Nombre: 'string', Finca: 'string', Codfinca: 'string', CodSuerte: 'string', Tablon: 'string', Nivel_padre: 'string',
    Area_geometrica_m2: 'number', Area_geometrica_ha: 'number', Cobertura_pct: 'number', Area_padre_geometrica_ha: 'number', Area_padre_declarada_ha: 'number', Creado: 'date',
  };
  return [
    ...subareaSourceFields.map((name) => ({ id: name, name, source: 'source' as const, type: sourceTypes[name], level: 'subarea' as const, sourceName: 'Áreas operativas' })),
    ...fields.filter(({ descriptor }) => descriptor.level === 'subarea').map(({ descriptor }) => descriptor),
  ];
}

export function effectiveSubareaAttributes(subarea: OperationalSubarea, fields: readonly OperationalAttributeField[]): OriginalAttributes {
  const result: Record<string, AttributeValue> = {
    Nombre: subarea.name,
    Finca: subarea.fincaName,
    Codfinca: subarea.codfinca,
    CodSuerte: subarea.codSuerte,
    Tablon: subarea.tablon ?? '',
    Nivel_padre: subarea.parentLevel,
    Area_geometrica_m2: subarea.areaM2,
    Area_geometrica_ha: subarea.areaHa,
    Cobertura_pct: subarea.coveragePercent,
    Area_padre_geometrica_ha: subarea.parentGeometryAreaM2 / 10_000,
    Area_padre_declarada_ha: subarea.parentDeclaredAreaHa ?? null,
    Creado: subarea.createdAt,
  };
  fields.filter(({ descriptor }) => descriptor.level === 'subarea').forEach((field) => {
    const value = field.valuesByEntityId[subarea.id];
    if (value !== undefined) {
      result[field.descriptor.id] = value;
      if (!(field.descriptor.name in result)) result[field.descriptor.name] = value;
    }
  });
  return result;
}

export function createSubareaLayer(subareas: readonly OperationalSubarea[], fields: readonly OperationalAttributeField[], previous?: VectorLayerModel, order = 0): VectorLayerModel {
  const records: VectorFeatureRecord[] = subareas.map((subarea) => {
    const attributes = effectiveSubareaAttributes(subarea, fields);
    const feature = subarea.feature.clone(); feature.setId(subarea.id); feature.setProperties({ ...attributes, genericFeatureId: subarea.id }, true);
    return { id: subarea.id, feature, attributes };
  });
  return {
    id: SUBAREA_LAYER_ID,
    kind: 'subarea',
    name: previous?.name ?? 'Áreas operativas parciales',
    sourceName: 'GeoStudio · áreas parciales',
    sourceFormat: 'SUBAREA',
    sourceCrs: 'EPSG:3857',
    assumedCrs: false,
    geometryTypes: [...new Set(records.map(({ feature }) => feature.getGeometry()?.getType()).filter(Boolean))] as VectorLayerModel['geometryTypes'],
    attributeFields: [...subareaSourceFields, ...fields.filter(({ descriptor }) => descriptor.level === 'subarea').map(({ descriptor }) => descriptor.id)],
    features: records,
    featuresById: new Map(records.map((record) => [record.id, record])),
    extent: records.length ? records.reduce<[number, number, number, number]>((extent, { feature }) => {
      const candidate = feature.getGeometry()!.getExtent(); return [Math.min(extent[0], candidate[0]!), Math.min(extent[1], candidate[1]!), Math.max(extent[2], candidate[2]!), Math.max(extent[3], candidate[3]!)];
    }, [Infinity, Infinity, -Infinity, -Infinity]) : null,
    visible: previous?.visible ?? true,
    opacity: previous?.opacity ?? 0.82,
    locked: true,
    order: previous?.order ?? order,
    style: previous?.style ?? { fillColor: '#ff8a3d', strokeColor: '#9f3d16', strokeWidth: 2.5, pointRadius: 6 },
    warnings: [],
    scopeMode: 'attributes',
  };
}

export function synchronizeSubareaLayer(layers: readonly VectorLayerModel[], subareas: readonly OperationalSubarea[], fields: readonly OperationalAttributeField[]): readonly VectorLayerModel[] {
  const previous = layers.find(({ id }) => id === SUBAREA_LAYER_ID);
  if (!subareas.length && !previous) return layers;
  const next = createSubareaLayer(subareas, fields, previous, layers.length);
  return normalizeLayerOrder(previous ? layers.map((layer) => layer.id === SUBAREA_LAYER_ID ? next : layer) : [...layers, next]);
}

export function renameSubarea(subareas: readonly OperationalSubarea[], id: string, name: string): readonly OperationalSubarea[] {
  const clean = name.trim(); if (!clean) throw new Error('El nombre del área parcial es obligatorio.');
  return subareas.map((item) => item.id === id ? { ...item, name: clean } : item);
}

export function deleteSubarea(subareas: readonly OperationalSubarea[], id: string): readonly OperationalSubarea[] {
  return subareas.filter((item) => item.id !== id);
}
